import { defineComponent, h, onBeforeUnmount, onMounted, reactive, ref, unref, watch } from 'vue';

import { classNames, supportsPassive, wrapperRaf } from '@components/utils';
import { useRender } from '@components/composable';

import type { PropType } from 'vue';

const MIN_SIZE = 20;

interface ScrollBarState {
  dragging: boolean;
  pageY: number | null;
  startTop: number | null;
  visible: boolean;
}

function getPageY(e: MouseEvent | TouchEvent) {
  return 'touches' in e ? e.touches[0].pageY : e.pageY;
}

export default defineComponent({
  name: 'ScrollBar',
  inheritAttrs: false,
  props: {
    prefixCls: String,
    scrollTop: Number,
    scrollHeight: Number,
    height: Number,
    count: Number,
    onScroll: {
      type: Function as PropType<(scrollTop: number) => void>
    },
    onStartMove: {
      type: Function as PropType<() => void>
    },
    onStopMove: {
      type: Function as PropType<() => void>
    }
  },
  setup(props) {
    const scrollbarRef = ref<HTMLDivElement>();
    const thumbRef = ref<HTMLDivElement>();
    const state = reactive<ScrollBarState>({
      dragging: false,
      pageY: null,
      startTop: null,
      visible: false
    });

    let moveRaf: number;
    let visibleTimeout: any = null;
    const delayHidden = () => {
      clearTimeout(visibleTimeout);
      state.visible = true;

      visibleTimeout = setTimeout(() => {
        state.visible = false;
      }, 2000);
    };

    const onScrollbarTouchStart = (e: TouchEvent) => {
      e.preventDefault();
    };

    const onContainerMouseDown = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const onMouseDown = (e: MouseEvent | TouchEvent) => {
      Object.assign(state, {
        dragging: true,
        pageY: getPageY(e),
        startTop: getTop()
      });

      props.onStartMove?.();
      patchEvents();
      e.stopPropagation();
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent | TouchEvent) => {
      const { dragging, pageY, startTop } = state;
      const { onScroll } = props;

      wrapperRaf.cancel(moveRaf);

      if (dragging) {
        const offsetY = getPageY(e) - pageY!;
        const newTop = startTop! + offsetY;

        const enableScrollRange = getEnableScrollRange();
        const enableHeightRange = getEnableHeightRange();

        const ptg = enableHeightRange ? newTop / enableHeightRange : 0;
        const newScrollTop = Math.ceil(ptg * enableScrollRange);
        moveRaf = wrapperRaf(() => {
          onScroll!(newScrollTop);
        });
      }
    };

    const onMouseUp = () => {
      const { onStopMove } = props;
      state.dragging = false;

      onStopMove!();
      removeEvents();
    };

    onMounted(() => {
      unref(scrollbarRef)?.addEventListener(
        'touchstart',
        onScrollbarTouchStart,
        supportsPassive ? ({ passive: false } as EventListenerOptions) : false
      );
      unref(thumbRef)?.addEventListener(
        'touchstart',
        onMouseDown,
        supportsPassive ? ({ passive: false } as EventListenerOptions) : false
      );
    });

    onBeforeUnmount(() => {
      removeEvents();
      clearTimeout(visibleTimeout);
    });

    watch(
      () => props.scrollTop,
      () => {
        delayHidden();
      },
      { flush: 'post' }
    );

    function patchEvents() {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);

      unref(thumbRef)!.addEventListener(
        'touchmove',
        onMouseMove,
        supportsPassive ? ({ passive: false } as EventListenerOptions) : false
      );
      unref(thumbRef)!.addEventListener('touchend', onMouseUp);
    }

    function removeEvents() {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      unref(scrollbarRef)?.removeEventListener(
        'touchstart',
        onScrollbarTouchStart,
        supportsPassive ? ({ passive: false } as EventListenerOptions) : false
      );
      if (unref(thumbRef)) {
        unref(thumbRef)?.removeEventListener(
          'touchstart',
          onMouseDown,
          supportsPassive ? ({ passive: false } as EventListenerOptions) : false
        );
        unref(thumbRef)?.removeEventListener(
          'touchmove',
          onMouseMove,
          supportsPassive ? ({ passive: false } as EventListenerOptions) : false
        );
        unref(thumbRef)?.removeEventListener('touchend', onMouseUp);
      }
      wrapperRaf.cancel(moveRaf);
    }

    function getTop() {
      const { scrollTop } = props;
      const enableScrollRange = getEnableScrollRange();
      const enableHeightRange = getEnableHeightRange();
      if (scrollTop === 0 || enableScrollRange === 0) {
        return 0;
      }
      const ptg = scrollTop! / enableScrollRange;
      return ptg * enableHeightRange;
    }

    function getSpinHeight() {
      const { height, scrollHeight } = props;
      let baseHeight = (height! / scrollHeight!) * 100;
      baseHeight = Math.max(baseHeight, MIN_SIZE);
      baseHeight = Math.min(baseHeight, height! / 2);
      return Math.floor(baseHeight);
    }

    function getEnableScrollRange() {
      const { scrollHeight, height } = props;
      return scrollHeight! - height! || 0;
    }

    function getEnableHeightRange() {
      const { height } = props;
      const spinHeight = getSpinHeight();
      return height! - spinHeight || 0;
    }

    function showScroll() {
      const { height, scrollHeight } = props;
      return scrollHeight! > height!;
    }

    useRender(() => {
      const { dragging, visible } = state;
      const { prefixCls } = props;
      const spinHeight = getSpinHeight() + 'px';
      const top = getTop() + 'px';
      const canScroll = showScroll();
      const mergedVisible = canScroll && visible;

      return h(
        'div',
        {
          ref: scrollbarRef,
          class: classNames(`${prefixCls}__scrollbar`, {
            [`${prefixCls}__scrollbar--show`]: canScroll
          }),
          style: {
            width: '8px',
            top: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: mergedVisible ? undefined : 'none'
          },
          onMousedown: onContainerMouseDown,
          onMousemove: delayHidden
        },
        h('div', {
          ref: thumbRef,
          class: classNames(`${prefixCls}__scrollbar-thumb`, {
            [`${prefixCls}__scrollbar-thumb--dragging`]: dragging
          }),
          style: {
            width: '100%',
            height: spinHeight,
            top,
            left: 0,
            position: 'absolute',
            background: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '99px',
            cursor: 'pointer',
            userSelect: 'none'
          },
          onMousedown: onMouseDown
        })
      );
    });
  }
});
